import { Box } from '@chakra-ui/react';

import { CheckboxWithField } from 'components/Checkbox';
import FormRow from 'components/FormRow';
import FormSection from 'components/FormSection';
import MarkdownWrapper from 'components/MarkdownWrapper/MarkdownWrapper';
import { validateSectionFields } from 'modules/Inquiry/Form/formConditions';
import {
  CONTACT_PERSON_ADVERTISING_CONSENT,
  CONTACT_PERSON_AFFILIATE_ADVERTISING_CONSENT,
  CONTACT_PERSON_BUSINESS_PARTNER_ADVERTISING_CONSENT,
  CONTACT_PERSON_SECURITY_CONSENT,
} from 'modules/Inquiry/Form/formFields';
import { useFieldValidators } from 'shared/hooks/useFieldValidators';
import { useTranslations } from 'utils/hooks/useTranslations';
import { combineValidators } from 'utils/validators';

type Props = {
  sectionNumber: number;
};

// EBS-40 / EBS-41: the BfS consent block is a single mandatory data-protection acknowledgement
// (→ privacy-policy-accepted) followed by three optional marketing consents — SozialBank,
// its sister companies, and the cooperation partner (Compeon) — plus a revocation notice.
export const validatebfsConsentSection = () =>
  validateSectionFields([CONTACT_PERSON_SECURITY_CONSENT]);

// ECBFS-2: the consent copy is long, so it renders one step below the body size (sizeM) and the
// consent blocks sit closer together than the default 3.5rem form-row spacing.
const CONSENT_TEXT_STYLE = 'sizeS';
const CONSENT_ROW_SPACE = '1.5rem';

// The checkbox labels carry an explicit `fontSize: lg` from Chakra's own checkbox theme (our
// `sizes.lg` only overrides the control box and label padding), so they do not inherit the
// wrapper's text style and have to be targeted directly. Scoped to this block, and deliberately
// only the label — the control keeps its usual size.
const CONSENT_LABEL_STYLES = {
  '.chakra-checkbox__label': { textStyle: CONSENT_TEXT_STYLE },
};

export const BfsTermsAndConditions = ({ sectionNumber }: Props) => {
  const { required, mustBeTrue } = useFieldValidators();
  const t = useTranslations('pages.companyDetails.sections.termsAndConditions.bfs');

  return (
    <FormSection sectionNumber={sectionNumber} title={t('title')}>
      <Box textStyle={CONSENT_TEXT_STYLE} sx={CONSENT_LABEL_STYLES}>
        {/* Mandatory data-protection acknowledgement → privacy-policy-accepted. */}
        <FormRow space={CONSENT_ROW_SPACE}>
          <CheckboxWithField
            name={CONTACT_PERSON_SECURITY_CONSENT}
            validate={combineValidators(required, mustBeTrue)}
            text={
              <span>
                {t('fields.contactPersonSecurityConsent.label', {
                  dataSecurity: (
                    <a href={t('fields.contactPersonSecurityConsent.links.dataSecurity.href')}>
                      {t('fields.contactPersonSecurityConsent.links.dataSecurity.text')}
                    </a>
                  ),
                })}
              </span>
            }
          />
        </FormRow>
        {/* Heading for the optional marketing consents. */}
        <FormRow space={CONSENT_ROW_SPACE}>
          <MarkdownWrapper text={t('fields.advertisingConsentHeading.label')} />
        </FormRow>
        {/* Optional consent to SozialBank marketing communications. */}
        <FormRow space={CONSENT_ROW_SPACE}>
          <CheckboxWithField
            name={CONTACT_PERSON_ADVERTISING_CONSENT}
            text={<span>{t('fields.contactPersonAdvertisingConsent.label')}</span>}
            optional
          />
        </FormRow>
        {/* Optional consent to share data with the sister companies. */}
        <FormRow space={CONSENT_ROW_SPACE}>
          <CheckboxWithField
            name={CONTACT_PERSON_AFFILIATE_ADVERTISING_CONSENT}
            text={<span>{t('fields.contactPersonAffiliateAdvertisingConsent.label')}</span>}
            optional
          />
        </FormRow>
        {/* Optional consent to be contacted by the cooperation partner (Compeon). */}
        <FormRow space={CONSENT_ROW_SPACE}>
          <CheckboxWithField
            name={CONTACT_PERSON_BUSINESS_PARTNER_ADVERTISING_CONSENT}
            text={<span>{t('fields.contactPersonBusinessPartnerAdvertisingConsent.label')}</span>}
            optional
          />
        </FormRow>
        {/* Revocation notice shown below the marketing consents. */}
        <FormRow space={CONSENT_ROW_SPACE}>
          <MarkdownWrapper text={t('fields.revocationNotice.label')} />
        </FormRow>
        {/* Mandatory-fields footnote. */}
        <FormRow space={CONSENT_ROW_SPACE}>
          <MarkdownWrapper text={t('fields.mandatoryFieldsNote.label')} />
        </FormRow>
      </Box>
    </FormSection>
  );
};
